/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { ReactStripeElements } from 'react-stripe-elements';
import Textbox from 'carbon-react/lib/components/textbox';
import Box from 'carbon-react/lib/components/box';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';
import { Checkbox } from 'carbon-react/lib/components/checkbox';

import { FormattedMessage, IntlShape } from '@futrli/localisation';

import { FormField, FormFieldKeys, PaymentFormData } from '../../types';
import styles from './payment-form.scss';
import { UICardElement } from '../../../core/components/stripe-styled-components';
import { provinces, states } from '../../constants';
import { UISearchSelectOption } from './payment-form';

interface Props {
  showRenderField: (fieldName: FormFieldKeys) => boolean;
  handleSubmit: (event: React.FormEvent) => Promise<void>;
  form: Partial<PaymentFormData>;
  error: stripe.PaymentMethodResponse['error'] | null;
  externalError?: JSX.Element | null;
  isDisabled: (fieldName: FormFieldKeys) => boolean;
  handleOnChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  formActions?: JSX.Element;
  formAdditionalFields?: JSX.Element;
  isSubmitting?: boolean;
  getOnSubmittingElement: () => JSX.Element;
  getTextFieldErrorMessage: (fieldName: FormFieldKeys) => string | undefined;
  isValidField: (fieldName: FormFieldKeys) => boolean;
  countries: UISearchSelectOption[];
  handleOnSearchFieldChange: (option: UISearchSelectOption | null) => void;
  handleStateProvinceChange: (option: UISearchSelectOption | null) => void;
  getCountryOptionFromCode: () => UISearchSelectOption | undefined;
  handleStripeElementChange: (event: ReactStripeElements.ElementChangeResponse) => void;
  intl: IntlShape;
  showNewForm: boolean;
}

export const PaymentFormFields = ({
  showRenderField,
  handleSubmit,
  form,
  error,
  externalError,
  isDisabled,
  handleOnChange,
  formActions,
  formAdditionalFields,
  isSubmitting,
  getOnSubmittingElement,
  getTextFieldErrorMessage,
  isValidField,
  countries,
  handleOnSearchFieldChange,
  handleStateProvinceChange,
  getCountryOptionFromCode,
  handleStripeElementChange,
  intl,
  showNewForm
}: Props) => {
  const showStateProvinceSelector = form.country === 'US' || form.country === 'CA';

  useEffect(() => {
    const stripeInputs = document.querySelectorAll('.__PrivateStripeElement-input');
    stripeInputs.forEach(input => {
      const inputElement = input as HTMLElement;
      inputElement.setAttribute('tabindex', '-1');
      inputElement.style.pointerEvents = 'none';
    });

    // Observe for dynamic updates
    const observer = new MutationObserver(() => {
      const updatedInputs = document.querySelectorAll('.__PrivateStripeElement-input');
      updatedInputs.forEach(input => {
        const inputElement = input as HTMLElement;
        inputElement.setAttribute('tabindex', '-1');
        inputElement.style.pointerEvents = 'none';
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const onCountryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleOnSearchFieldChange(countries.find(c => c.value === event.target.value) || null);
    },
    [countries, handleOnSearchFieldChange]
  );

  const onProvinceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const options = form.country === 'CA' ? provinces : states;
      handleStateProvinceChange(options.find(c => c.value === event.target.value) || null);
    },
    [form.country, handleStateProvinceChange]
  );

  return (
    <form
      onSubmit={handleSubmit}
      style={showNewForm ? { marginTop: 32, maxWidth: 480, height: '100%' } : { height: '100%' }}
    >
      {error && error.code && <p className={classNames(styles.errorContainer, 'uiTypeLabelRed')}>{error.message}</p>}

      {externalError && <p className={classNames(styles.errorContainer, 'uiTypeLabelRed')}>{externalError}</p>}

      {showRenderField(FormField.paymentMethodHolderName) && (
        <Box width="100%" mb="24px">
          <Textbox
            size="large"
            id={FormField.paymentMethodHolderName}
            name={FormField.paymentMethodHolderName}
            placeholder={intl.formatMessage({
              id: 'common.enter_name_here',
              defaultMessage: 'Enter name here'
            })}
            label={intl.formatMessage({
              id: 'billing.payment_form_card_holder_name_label',
              defaultMessage: "Card holder's name"
            })}
            value={form.paymentMethodHolderName}
            onChange={handleOnChange}
            disabled={isDisabled(FormField.paymentMethodHolderName)}
            error={
              !isValidField(FormField.paymentMethodHolderName)
                ? getTextFieldErrorMessage(FormField.paymentMethodHolderName)
                : undefined
            }
          />
        </Box>
      )}

      {showRenderField(FormField.addressLine1) && (
        <Box width="100%">
          <Textbox
            size="large"
            id={FormField.addressLine1}
            name={FormField.addressLine1}
            label={intl.formatMessage({ id: 'billing.billing_address', defaultMessage: 'Billing address' })}
            placeholder={intl.formatMessage({
              id: 'billing.address_line1_placeholder',
              defaultMessage: 'Address line 1'
            })}
            value={form.addressLine1}
            onChange={handleOnChange}
            disabled={isDisabled(FormField.addressLine1)}
            error={!isValidField(FormField.addressLine1) ? getTextFieldErrorMessage(FormField.addressLine1) : undefined}
            mb="8px"
          />
        </Box>
      )}

      {showRenderField(FormField.addressLine2) && (
        <Box width="100%">
          <Textbox
            size="large"
            id={FormField.addressLine2}
            name={FormField.addressLine2}
            placeholder={intl.formatMessage({
              id: 'billing.address_line2_placeholder',
              defaultMessage: 'Address line 2 (optional)'
            })}
            value={form.addressLine2}
            onChange={handleOnChange}
            disabled={isDisabled(FormField.addressLine2)}
            error={!isValidField(FormField.addressLine2) ? getTextFieldErrorMessage(FormField.addressLine2) : undefined}
            mb="8px"
          />
        </Box>
      )}

      {showRenderField(FormField.city) && (
        <Box width="100%">
          <Textbox
            size="large"
            id={FormField.city}
            name={FormField.city}
            placeholder={intl.formatMessage({
              id: 'billing.city_placeholder',
              defaultMessage: 'City / Town'
            })}
            value={form.city}
            onChange={handleOnChange}
            disabled={isDisabled(FormField.city)}
            error={!isValidField(FormField.city) ? getTextFieldErrorMessage(FormField.city) : undefined}
            mb="8px"
          />
        </Box>
      )}

      {showRenderField(FormField.postalCode) && (
        <Box width="100%">
          <Textbox
            size="large"
            id={FormField.postalCode}
            name={FormField.postalCode}
            placeholder={intl.formatMessage({
              id: 'billing.postal_code_placeholder',
              defaultMessage: 'Postcode / Zip'
            })}
            value={form.postalCode}
            onChange={handleOnChange}
            disabled={isDisabled(FormField.postalCode)}
            error={!isValidField(FormField.postalCode) ? getTextFieldErrorMessage(FormField.postalCode) : undefined}
            mb="8px"
          />
        </Box>
      )}

      {showRenderField(FormField.country) && (
        <Box width="100%" mb="16px">
          <FilterableSelect
            id={FormField.country}
            disabled={isDisabled(FormField.country)}
            label={intl.formatMessage({
              id: 'common.country',
              defaultMessage: 'Country'
            })}
            placeholder={intl.formatMessage({
              id: 'billing.country_placeholder',
              defaultMessage: 'Search your country...'
            })}
            size="large"
            value={
              typeof getCountryOptionFromCode()?.value !== 'undefined' ? String(getCountryOptionFromCode()?.value) : ''
            }
            onChange={onCountryChange}
          >
            {countries.map((c: UISearchSelectOption) => (
              <Option
                key={`${c.value}-${c.label}`}
                id={`${c.value}-${c.label}`}
                text={c.label}
                value={String(c.value)}
              />
            ))}
          </FilterableSelect>
        </Box>
      )}

      {showStateProvinceSelector && (
        <Box width="100%" mb="16px">
          <FilterableSelect
            id={FormField.state}
            onChange={onProvinceChange}
            placeholder={
              form.country === 'CA'
                ? intl.formatMessage({
                    id: 'billing.province_placeholder',
                    defaultMessage: 'Search your province...'
                  })
                : intl.formatMessage({
                    id: 'billing.state_placeholder',
                    defaultMessage: 'Search your state...'
                  })
            }
            size="large"
            value={typeof form.state?.value !== 'undefined' ? String(form.state?.value) : ''}
          >
            {(form.country === 'CA' ? provinces : states).map((c: UISearchSelectOption) => (
              <Option
                key={`${c.value}-${c.label}`}
                id={`${c.value}-${c.label}`}
                text={c.label}
                value={String(c.value)}
              />
            ))}
          </FilterableSelect>
        </Box>
      )}

      {showRenderField(FormField.country) && (
        <>
          <Box width="100%">
            <Checkbox
              size="large"
              name={FormField.notVatRegistered}
              value={String(form.notVatRegistered)}
              checked={Boolean(form.notVatRegistered)}
              onChange={handleOnChange}
              disabled={Boolean(form.vat && form.vat.length > 0)}
              label={
                <div style={{ fontWeight: 500, fontSize: 16 }}>
                  <FormattedMessage id="billing.not_vat_gst_registered" defaultMessage="Not VAT/GST registered" />
                </div>
              }
              mb="8px"
            />
          </Box>
          <Box width="100%" mb="16px">
            <Textbox
              size="large"
              id={FormField.vat}
              name={FormField.vat}
              placeholder={intl.formatMessage({
                id: 'billing.vat_placeholder',
                defaultMessage: 'Enter the characters following your country code'
              })}
              label={intl.formatMessage({ id: 'billing.vat_gst_label', defaultMessage: 'VAT/GST number' })}
              value={form.vat}
              onChange={handleOnChange}
              disabled={isDisabled(FormField.vat) || Boolean(form.notVatRegistered)}
              error={!isValidField(FormField.vat) ? getTextFieldErrorMessage(FormField.vat) : undefined}
            />
          </Box>
        </>
      )}

      {showRenderField(FormField.paymentMethodId) && (
        <div aria-hidden="true" style={{ width: '100%' }}>
          <UICardElement
            containerClassName={styles.formFieldWithLabel}
            hidePostalCode={true}
            onChange={handleStripeElementChange}
            label={<FormattedMessage id="billing.card_details" defaultMessage="Card details" />}
            disabled={isDisabled(FormField.paymentMethodId)}
          />
        </div>
      )}
      {formAdditionalFields}
      {formActions}
      {isSubmitting && getOnSubmittingElement()}
    </form>
  );
};
